package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** Ask to change the address you sign in with. */
@Serializable
data class ChangeEmailRequest(val newEmail: String)

/** Confirm it, from the link sent to the **new** address. */
@Serializable
data class ConfirmEmailChangeRequest(val token: String)

/**
 * What closing your account would actually do, computed live.
 *
 * Shown before the button rather than after it, and [soleOwnerOf] is why: sole ownership has to be
 * resolved first, so the button stays disabled with the reason beside it rather than offering a
 * click that then explains.
 */
@Serializable
data class CloseAccountPreviewDto(
    /** Workspaces where you are the only owner. These block closing until somebody else owns them. */
    val soleOwnerOf: List<String> = emptyList(),
    /** Workspaces you merely belong to. Leaving these is automatic and costs nobody anything. */
    val memberOf: List<String> = emptyList(),
    val apiTokens: Int = 0,
    /**
     * When the deletion would happen if requested now.
     *
     * Seven days out. An irreversible thing done at 2am should be recoverable at 9am.
     */
    val scheduledFor: String? = null,
    /** Set when a closure is already pending, so the page offers cancelling instead of requesting. */
    val pending: Boolean = false,
)

/**
 * What somebody wants to hear about.
 *
 * **Security emails are absent from this list on purpose**, not disabled in it: an attacker who can
 * silence the alert about their own intrusion has been handed the last piece.
 */
@Serializable
enum class NotificationEvent(val id: String, val description: String, val workspaceLevel: Boolean) {
    REVIEW_REQUESTED("review_requested", "Something is waiting for your review", false),
    CHANGES_REQUESTED("changes_requested", "A reviewer sent your work back", false),
    MENTIONED("mentioned", "Somebody mentioned you in a comment", false),
    LOCALE_COMPLETED("locale_completed", "A language reached 100%", false),
    MEMBER_JOINED("member_joined", "Somebody joined the workspace", true),
    CREDITS_LOW("credits_low", "Machine-translation credits are running out", true),
    PAYMENT_FAILED("payment_failed", "A payment did not go through", true),
    ;

    companion object {
        fun byId(id: String): NotificationEvent? = entries.firstOrNull { it.id.equals(id, ignoreCase = true) }
    }
}

@Serializable
data class NotificationSettingDto(
    val event: NotificationEvent,
    val enabled: Boolean,
)

@Serializable
data class NotificationSettingsDto(val settings: List<NotificationSettingDto> = emptyList())

/** Turn one on or off. */
@Serializable
data class UpdateNotificationRequest(val event: NotificationEvent, val enabled: Boolean)

/** The display name, and nothing else. The address has its own confirmed, two-sided flow. */
@Serializable
data class UpdateProfileRequest(val name: String)
